import createHttp from '@/config/http';
import type { BenefitVerification } from '@/types/benefitsVerification';
import type { ProductSku, UpdateBVProductPayload } from '@/types/product';
import type {
	CreateOrderRequest,
	Request,
	RequestsResponse,
	UpdateOrderRequest,
} from '@/types/requests';
import type { ResponseMetaData } from '@/types/service';

// localhost:3002 (Orders microservice port)
const http = createHttp(import.meta.env.VITE_ORDERS_URL);

export const getRequestsProducts = async (
	page = 1,
	limit = 15,
	globalFilters = '[]',
): Promise<{
	items: ProductSku[];
	meta: ResponseMetaData;
}> => {
	const response = await http.get('/orders/requests/products', {
		params: {
			page,
			limit,
			globalFilters,
		},
	});
	return {
		items: response.data.data.items,
		meta: {
			...response.data.data.metaData,
			itemsPerPage: response.data.data.metaData.recordsPerPage,
		},
	};
};

export const getRequests = async (
	page = 1,
	limit = 15,
	columnsFilters = '[]',
	search = '',
	status: string | number = '',
	globalFilters = '[]',
	sort = '',
): Promise<{
	items: Request[];
	meta: ResponseMetaData;
}> => {
	const response = await http.get<RequestsResponse>('/orders/requests', {
		params: {
			page,
			limit,
			columnsFilters,
			search,
			status,
			globalFilters,
			sort,
		},
	});
	return {
		items: response.data.data.items,
		meta: {
			...response.data.data.metaData,
			itemsPerPage: response.data.data.metaData.recordsPerPage,
		},
	};
};

export const createOrderRequest = async (payload: {
	orderRequest: CreateOrderRequest;
	bvPayload: {
		bvId: string;
		payload: Partial<BenefitVerification>;
	};
	bvProductPayload: {
		priorAuthId: string;
		payload: UpdateBVProductPayload;
	};
}): Promise<any> => {
	const response = await http.post('/orders/create-request', payload);
	return response.data;
};

/**
 * Create bulk order - sends ONE request to create multiple orders
 * Backend handles creating separate orders for each product
 * @param payload - Bulk order payload with products array
 */
export const createBulkOrderRequest = async (payload: any): Promise<any> => {
	const response = await http.post('/orders/create-bulk-request', payload);
	return response.data;
};

/**
 * Get single order request details by ID from database
 * @param orderId - The order request ID
 */
export const getOrderRequestById = async (orderId: string): Promise<any> => {
	const response = await http.get(`/orders/db/${orderId}`);
	return response.data;
};

/**
 * Get all order requests that belong to the same bulk order
 * Bulk orders share the same clinic, doctor, PO, date, etc. but have different products
 * @param orderId - Any order ID from the bulk order group
 */
export const getBulkOrderRequests = async (orderId: string): Promise<any> => {
	const response = await http.get(`/orders/bulk-group/${orderId}`);
	return response.data;
};

/**
 * Update an existing order request (only quantity and comments)
 * @param orderId - The order request ID
 * @param payload - Updated order data (only ta_quantity and ta_comments allowed)
 */
export const updateOrderRequest = async (
	orderId: string,
	payload: UpdateOrderRequest,
): Promise<any> => {
	const response = await http.patch(
		`/orders/update-request/${orderId}`,
		payload,
	);
	return response.data;
};

/**
 * Update multiple orders in a bulk order group (only quantities and comments)
 * @param payload - Updated bulk order data with products array (orderId + quantity) and shared comments
 */
export const updateBulkOrderRequest = async (payload: {
	ta_comments?: string;
	products: Array<{ orderId: string; ta_quantity: string }>;
}): Promise<any> => {
	const response = await http.patch('/orders/update-bulk-request', payload);
	return response.data;
};

/**
 * Get order with shipment details (tracking information)
 * @param orderId - The order request ID
 */
export const getOrderShipments = async (orderId: string): Promise<any> => {
	const response = await http.get(`/orders/db/${orderId}?view=details`);
	return response.data;
};

export const getRecentOrderRequests = async (
	page = 1,
	limit = 50,
	clinics: string[] = [],
	port = '',
): Promise<{
	items: Request[];
	meta: ResponseMetaData;
	newOrders: number;
}> => {
	const response = await http.get('/orders/latest-requests', {
		params: {
			page,
			limit,
			clinicIds: clinics.join(','),
			port
		},
	});
	return {
		items: response.data.data.items,
		newOrders: response.data.data.newOrders,
		meta: {
			...response.data.data.metaData,
			itemsPerPage: response.data.data.metaData.recordsPerPage,
		},
	};
};

export const getOrderRequestScoreCards = async (
	page = 1,
	limit = 50,
	clinics: string[] = [],
	port = '',
	dateRangeFilter = '',
): Promise<{
	items: Request[];
	meta: ResponseMetaData;
	newOrders: number;
}> => {
	const response = await http.get('/orders/latest-requests/score-cards', {
		params: {
			page,
			limit,
			clinicIds: clinics.join(','),
			port,
			dateRangeFilter: dateRangeFilter || '',
		},
	});
	return {
		items: response.data.data.items,
		newOrders: response.data.data.newOrders,
		meta: {
			...response.data.data.metaData,
			itemsPerPage: response.data.data.metaData.recordsPerPage,
		},
	};
};